import { GridRenderCellParams } from '@mui/x-data-grid';
import { Typography } from '@mui/material';
import { Trans } from 'react-i18next';
import formatBudgetDifferenceLabel from 'pages/calculator/MediaPlanning/Plan/Countries/NestedCountryTabs/OneReach/utils/formatBudgetDifferenceLabel';
import { CalculatedOneReach } from 'api/services/mediaPlanning/types';
import applyCellTextStyle from 'pages/calculator/MediaPlanning/Plan/Countries/NestedCountryTabs/OneReach/utils/applyCellTextStyle';
import convertDecimalToPercentValue from 'pages/calculator/MediaPlanning/Plan/Countries/utils/convertDecimalToPercentValue';
import { formatNumberWithSpaces } from 'pages/calculator/MediaPlanning/common/BudgetTable/utils/formatNumberWithSpaces';

const defineOneReachColumns = (data: CalculatedOneReach['results']) => {
  const getSelectedBudgetForTouchpoint = (touchpointName: string) => {
    return data.find(
      (rowData) =>
        rowData.isSelected && rowData.touchpointName === touchpointName
    )?.budget;
  };

  return [
    {
      field: 'touchpointName',
      headerName: 'TOUCHPOINT',
      flex: 1.1,
      sortable: false,
      renderCell: (params: GridRenderCellParams) => (
        <Typography
          sx={{
            ...applyCellTextStyle(params.row),
          }}
        >
          {params.value}
        </Typography>
      ),
    },
    {
      field: 'saturation',
      headerName: 'SATURATION',
      flex: 0.8,
      sortable: true,
      renderCell: (params: GridRenderCellParams) => (
        <Typography
          sx={{
            ...applyCellTextStyle(params.row),
          }}
        >
          {params.value
            ? `${convertDecimalToPercentValue(params.value)}%`
            : '-'}
        </Typography>
      ),
    },
    {
      field: 'grp',
      headerName: 'GRP',
      flex: 0.8,
      sortable: true,
      renderCell: (params: GridRenderCellParams) => (
        <Typography
          sx={{
            ...applyCellTextStyle(params.row),
          }}
        >
          {params.value ? params.value : '-'}
        </Typography>
      ),
    },
    {
      field: 'oneReach',
      headerName: 'ONE REACH',
      flex: 0.7,
      sortable: false,
      type: 'number',
      renderCell: (params: GridRenderCellParams) => {
        if (params.value !== undefined) {
          return (
            <Typography
              sx={{
                ...applyCellTextStyle(params.row),
              }}
            >
              {`${convertDecimalToPercentValue(params.value)}%`}
            </Typography>
          );
        }
        return <Typography>-</Typography>;
      },
    },
    {
      field: 'budget',
      headerName: 'BUDGET',
      flex: 1.2,
      sortable: false,
      type: 'number',
      renderCell: (params: GridRenderCellParams) => {
        if (params.value !== undefined) {
          const budget = params.value;
          // TODO: remove this after backend is ready
          if (budget === 0) {
            return <Typography>N/A</Typography>;
          }
          const selectedBudget =
            getSelectedBudgetForTouchpoint(params.row.touchpointName) || budget;
          const budgetDifference = budget - selectedBudget;

          return (
            <Typography
              sx={{
                ...applyCellTextStyle(params.row),
                span: {
                  pl: '2px',
                  fontSize: '10px',
                },
              }}
            >
              {`${formatNumberWithSpaces(budget.toFixed(0))}`}
              <span>PLN</span>
              {formatBudgetDifferenceLabel(budgetDifference)}
            </Typography>
          );
        }
        return <Typography>-</Typography>;
      },
    },
    {
      field: 'status',
      headerName: '',
      flex: 1.2,
      sortable: false,
      renderCell: (params: GridRenderCellParams) => {
        if (params.row.isSelected) {
          return (
            <Typography sx={{ fontWeight: 'bold', pl: '25px' }}>
              <Trans i18nKey="mediaPlanning.currentlySelected" />
            </Typography>
          );
        }
        return null;
      },
    },
  ];
};

export default defineOneReachColumns;
